/**
 * API Client Module
 * Handles all API communications for the eKYC SDK
 */

import axios from 'axios';
import { API_ENDPOINTS, ERROR_CODES, ERROR_MESSAGES, NID_AUTH_CREDENTIALS } from '../config/constants';

class ApiClient {
  constructor() {
    this.axiosInstance = axios.create({
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    this.authToken = null;
    this.tokenExpiry = null;
  }

  /**
   * Call NID Face Verification API
   * Used for both OCR verification and liveness verification
   */
  async callNidFaceVerificationApi(data) {
    try {
      // Get API key
      const apiKey = await this.getApiKey();

      const response = await this.axiosInstance.post(API_ENDPOINTS.NID_FACE_VERIFICATION, data, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': apiKey
        }
      });

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('NID Face Verification API Error:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers,
        config: error.config
      });

      if (error.code === 'ECONNABORTED') {
        throw {
          errorCode: ERROR_CODES.E102,
          message: ERROR_MESSAGES.E102
        };
      }

      // Check if it's a CORS error
      if (error.message && error.message.toLowerCase().includes('cors')) {
        throw {
          errorCode: ERROR_CODES.E500,
          message: 'CORS error: The server does not allow requests from this origin. Please check server CORS configuration.'
        };
      }

      throw {
        errorCode: ERROR_CODES.E500,
        message: error.message || ERROR_MESSAGES.E500
      };
    }
  }

  /**
   * Get API Key from authentication endpoint
   * Caches the token and handles expiry
   */
  async getApiKey() {
    // Check if API key exists and is not expired
    if (this.authToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.authToken;
    }

    // Request new API key
    try {
      const response = await this.axiosInstance.post(API_ENDPOINTS.NID_AUTH, NID_AUTH_CREDENTIALS, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.apiKey) {
        this.authToken = response.data.apiKey;
        // Set expiry time (expiresIn is in seconds, convert to milliseconds)
        this.tokenExpiry = Date.now() + (response.data.expiresIn * 1000);
        return this.authToken;
      } else {
        console.error('Invalid auth response:', response.data);
        throw new Error('Invalid auth response - no apiKey found');
      }
    } catch (error) {
      console.error('Auth Error Details:', {
        message: error.message,
        code: error.code,
        response: error.response?.data,
        status: error.response?.status,
        config: error.config
      });

      if (error.response) {
        // Server responded with error status
        throw new Error(`Auth failed: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`);
      } else if (error.request) {
        // Request made but no response received
        throw new Error('Auth failed: No response from server. Please check your network connection.');
      } else {
        // Something else happened
        throw new Error(`Auth failed: ${error.message}`);
      }
    }
  }

  /**
   * Configure API endpoints
   */
  configureEndpoints(endpoints) {
    if (endpoints.NID_FACE_VERIFICATION) {
      API_ENDPOINTS.NID_FACE_VERIFICATION = endpoints.NID_FACE_VERIFICATION;
    }
    if (endpoints.NID_AUTH) {
      API_ENDPOINTS.NID_AUTH = endpoints.NID_AUTH;
    }
  }
}

// Export singleton instance
export default new ApiClient();
